import type { ChatMessage } from "@/lib/chat/map-api-message"
import type { ChatReplyToPreview } from "@/lib/chat/reply-to-preview"

const IMAGE_TOKENS = new Set(["[image]", "[images]"])
const LISTING_TOKENS = new Set(["[listing]"])
const ORDER_TOKENS = new Set(["[order]"])

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export type ChatReplyPreview = ChatReplyToPreview & {
  listing?: unknown | null
  order?: unknown | null
}

export function isUuid(value: string | null | undefined): boolean {
  if (!value) return false
  return UUID_RE.test(value.trim())
}

export type ReplySenderContext = {
  currentUserId?: string | null
  participantNames?: Readonly<Record<string, string>>
}

function resolveDisplayName(name: string | null | undefined): string | null {
  const trimmed = name?.trim()
  if (!trimmed || isUuid(trimmed)) return null
  return trimmed
}

export function resolveChatSenderDisplayName(
  rawName: string | null | undefined,
  senderId: string | null | undefined,
  ctx?: ReplySenderContext,
  fallback = "User",
): string {
  const fromName = resolveDisplayName(rawName)
  if (fromName) return fromName

  const senderKey = senderId?.trim()
  if (senderKey && ctx?.participantNames) {
    const fromParticipant = resolveDisplayName(ctx.participantNames[senderKey])
    if (fromParticipant) return fromParticipant
  }

  const fromFallback = resolveDisplayName(fallback)
  if (fromFallback) return fromFallback

  if (senderKey && ctx?.currentUserId && senderKey === ctx.currentUserId) {
    return "You"
  }

  return "User"
}

export function summarizeMessageContent(
  message: Pick<ChatMessage, "content" | "imageUrl" | "listing" | "order">,
): string {
  const token = (message.content ?? "").trim().toLowerCase()
  if (message.listing || LISTING_TOKENS.has(token)) {
    const listing = message.listing as { title?: string | null } | null | undefined
    return listing?.title?.trim() || "Listing"
  }
  if (message.order || ORDER_TOKENS.has(token)) {
    const order = message.order as { orderNumber?: string | null; orderId?: string | null } | null | undefined
    const num = order?.orderNumber ?? order?.orderId
    if (num && !isUuid(String(num))) return `#${num}`
    return "Order"
  }
  if (message.imageUrl || IMAGE_TOKENS.has(token)) {
    return "Photo"
  }
  const text = (message.content ?? "").trim()
  if (text.length > 0 && !isUuid(text)) return text
  return "Message"
}

export function truncateReplySnippet(text: string, max = 120): string {
  const compact = text.replace(/\s+/g, " ").trim()
  if (compact.length <= max) return compact
  return `${compact.slice(0, max - 1)}…`
}

export function isReplyToSelf(
  replyTo: Pick<ChatReplyPreview, "senderId">,
  ctx?: ReplySenderContext,
): boolean {
  const senderKey = replyTo.senderId?.trim()
  const currentUserId = ctx?.currentUserId?.trim()
  return Boolean(senderKey && currentUserId && senderKey === currentUserId)
}

export function buildReplyPreviewLabel(
  replyTo: ChatReplyPreview,
  fallbackSenderName: string,
  ctx?: ReplySenderContext,
): { senderLabel: string; snippet: string } {
  const senderLabel = resolveChatSenderDisplayName(
    replyTo.senderName,
    replyTo.senderId,
    ctx,
    fallbackSenderName,
  )
  const snippet = truncateReplySnippet(summarizeMessageContent(replyTo))
  return { senderLabel, snippet }
}

export function toReplyPreview(message: ChatMessage, ctx?: ReplySenderContext): ChatReplyPreview {
  const senderId = message.senderId ?? ""
  const senderName = resolveChatSenderDisplayName(message.user?.name, senderId, ctx)
  return {
    id: message.id,
    content: message.content ?? "",
    senderId,
    senderName,
    imageUrl: message.imageUrl ?? null,
    listingId: message.listingId ?? (message.listing as { id?: string } | null | undefined)?.id ?? null,
    listing: message.listing ?? null,
    order: message.order ?? null,
    orderId: (message.order as { orderId?: string | null } | null | undefined)?.orderId ?? null,
    sellerOrderId:
      (message.order as { sellerOrderId?: string | null } | null | undefined)?.sellerOrderId ?? null,
  }
}

export function replyToAsPreview(replyTo: ChatReplyToPreview): ChatReplyPreview {
  return { ...replyTo, listing: null, order: null }
}

export function isCopyableMessage(message: ChatMessage): boolean {
  const token = (message.content ?? "").trim().toLowerCase()
  if (message.listing || message.order || message.imageUrl) return false
  if (LISTING_TOKENS.has(token) || ORDER_TOKENS.has(token) || IMAGE_TOKENS.has(token)) return false
  return (message.content ?? "").trim().length > 0
}

export function findMessageById(messages: ChatMessage[], messageId: string): ChatMessage | null {
  return messages.find((m) => m.id === messageId) ?? null
}
